import {
  type Username,
  isFacilityKey,
  isOrganizationKey,
  isUserKey,
  toUniversalError,
} from "@packages/core";
import { Amplify } from "aws-amplify";
import { AuthError, getCurrentUser, signIn, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { create } from "zustand";

import { ENVIRONMENT_VARIABLE } from "#environment-variable";

/** 認証ストア */
interface AuthenticationStore {
  username?: Username;
  initialized: boolean;
  loading: boolean;
  /** **ログインフラグ** ログイン済みの場合、`true`。 */
  loggedIn: boolean;
  /** **ログアウトフラグ** ログアウト済みの場合、`true`。 */
  loggedOut: boolean;

  /**
   * ログインする。
   * @param username ユーザー名
   * @param password パスワード
   * @returns ユーザー名
   */
  logIn: { (username: string, password: string): Promise<Username> };
  logOut: { (): Promise<void> };
}

/** 認証ストアを使用する。 */
export const useAuthenticationStore = create<AuthenticationStore>(function (set) {
  // Amplifyを初期化する。
  Amplify.configure({
    Auth: {
      Cognito: {
        identityPoolId: ENVIRONMENT_VARIABLE.COGNITO_IDENTITY_POOL_ID,
        userPoolClientId: ENVIRONMENT_VARIABLE.COGNITO_USER_POOL_CLIENT_ID,
        userPoolId: ENVIRONMENT_VARIABLE.COGNITO_USER_POOL_ID,
      },
    },
  });

  // Amazon Cognitoの認証イベントを購読する。
  Hub.listen("auth", function (data) {
    console.debug("認証イベントが発生しました。", data);

    const { payload } = data;
    switch (payload.event) {
      case "signedIn": {
        console.debug("ユーザー[" + payload.data.username + "]としてサインインしました。", payload);

        const {
          data: { username },
        } = payload;

        if (isFacilityKey(username) || isOrganizationKey(username) || isUserKey(username)) {
          set({ username });
        }
        break;
      }
      case "signedOut":
        console.debug("サインアウトしました。", payload);
        break;
      case "tokenRefresh":
        console.debug("認証トークンが更新されました。", payload);
        break;
      case "tokenRefresh_failure":
        console.debug("認証トークンの更新に失敗しました。", payload);
        break;
      case "signInWithRedirect":
        console.debug("signInWithRedirect APIが正常に解決されました。", payload);
        break;
      case "signInWithRedirect_failure":
        console.debug("signInWithRedirect APIの解決に失敗しました。", payload);
        break;
      case "customOAuthState":
        console.debug("CognitoHosted UIからカスタムステートが返されました。", payload);
        break;
      default:
        console.warn("不明な認証イベントが発生しました。", payload);
        break;
    }
  });

  getCurrentUser()
    .then(function (currentUser) {
      console.debug("現在のユーザーを取得しました。", currentUser);

      const { username } = currentUser;

      if (isFacilityKey(username) || isOrganizationKey(username) || isUserKey(username)) {
        set({ initialized: true, loggedIn: true, username });
      } else {
        set({ initialized: true, loggedIn: false });
      }
    })
    .catch(function (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "UserUnAuthenticatedException":
            console.debug("ログインしていません。", error);
            return;
          default:
            throw toUniversalError(error, {
              message: "現在のユーザーの取得中にエラーが発生しました。",
            });
        }
      } else {
        throw toUniversalError(error, {
          message: "現在のユーザーの取得中にエラーが発生しました。",
        });
      }
    })
    .finally(function () {
      set({ initialized: true });
    });

  return {
    initialized: false,
    loading: false,
    loggedIn: false,
    loggedOut: false,

    async logIn(username, password) {
      let response;
      try {
        set({ loggedOut: false });
        response = await signIn({
          options: { authFlowType: "USER_SRP_AUTH", preferredChallenge: "PASSWORD_SRP" },
          password,
          username,
        });

        console.debug("サインインに成功しました。", response);
      } catch (error) {
        throw toUniversalError(error, {
          data: { private: { password, username } },
          message: "サインインに失敗しました。",
        });
      }

      switch (response.nextStep.signInStep) {
        case "DONE":
          set({ loggedIn: true });
          break;
        default:
          throw new Error("サインインに失敗しました。");
      }

      let currentUser;
      try {
        currentUser = await getCurrentUser();
      } catch (error) {
        console.error("現在のユーザーの取得に失敗しました。", error);
        throw toUniversalError(error, {
          message: "現在のユーザーの取得に失敗しました。",
        });
      }

      if (
        isFacilityKey(currentUser.username) ||
        isOrganizationKey(currentUser.username) ||
        isUserKey(currentUser.username)
      ) {
        set({ username: currentUser.username });
      } else {
        throw new Error("サインインに失敗しました。");
      }
      return currentUser.username;
    },

    async logOut() {
      set({ loggedOut: true });
      return signOut();
    },
  };
});

/** @deprecated 代わりに`useUsername`を使用する。 */
export function useCurrentUsername() {
  return useAuthenticationStore(({ username }) => username);
}

/**
 * 現在ログイン中のユーザー名を使用する。
 * - ログインしている場合、ユーザー名を返す。
 * - ログインしていない場合、`null`または`undefined`を返す。
 *   - 初期化が完了している場合は`null`を返す。
 *   - 初期化が完了していない場合は`undefined`を返す。
 * @returns
 */
export function useUsername() {
  return useAuthenticationStore(({ initialized, username }) =>
    username ? username : initialized ? null : undefined,
  );
}
